import { onMounted, onBeforeUnmount } from "vue";
import { UserTracking } from "@/utils/UserTracking";
import { useSessionStore } from "@/composables/useSessionStore";

/**
 * Tracks browser tab lifecycle events (tab visibility and page unload) to
 * Amplitude so we can correlate session failures with the user leaving the
 * tab, sleeping the device, or closing the window mid-recording.
 *
 * Emits two events:
 *   - `noa_session_tab_visibility_changed` — fires only on real `hidden <-> visible`
 *     transitions (not on every event, to avoid duplicates on mobile bfcache hops).
 *   - `noa_session_tab_page_hide` — fires on `pagehide` (covers tab close,
 *     navigation away, mobile background). Includes `persisted` to distinguish
 *     bfcache from real unload.
 *
 * `session_id` defaults to "no-active-session" when no recorder session is
 * active so we can still see "doctor closed plugin without ever recording"
 * patterns in Amplitude.
 */
export function useBrowserLifecycle(): void {
  const sessionStore = useSessionStore();
  let lastVisibilityState: DocumentVisibilityState = document.visibilityState;

  const onVisibilityChange = () => {
    const current = document.visibilityState;
    if (current === lastVisibilityState) return;
    lastVisibilityState = current;

    UserTracking.trackEvent("noa_session_tab_visibility_changed", {
      state: current,
      session_id: sessionStore.getRecorderSessionId() ?? "no-active-session",
      page_url: window.location.href,
    });
  };

  const onPageHide = (event: PageTransitionEvent) => {
    UserTracking.trackEvent("noa_session_tab_page_hide", {
      persisted: String(event.persisted),
      session_id: sessionStore.getRecorderSessionId() ?? "no-active-session",
      page_url: window.location.href,
    });
  };

  onMounted(() => {
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  });
}
